import { Provider } from '@angular/core';
import { defer, Observable, of, throwError, timer } from 'rxjs';
import { delay, map, mergeMap } from 'rxjs/operators';
import {
  SimulationRequest,
  SimulationResult,
} from '../models/site-simulation.model';
import { SiteSimulationRepository } from './site-simulation.repository';
import { createSeededRandom, runSimulation } from './site-simulation.engine';

/** Stream behavior of the mock, used to exercise the host's async states. */
export type SiteSimulationMockMode = 'data' | 'empty' | 'error';

export interface SiteSimulationMockConfig {
  /** Lets the host render a loading state first. */
  delayMs?: number;
  /** `empty` emits zero year rows and `error` errors after the delay, so both states are
   * reachable from the host. */
  mode?: SiteSimulationMockMode;
  /** Threaded into the engine's RNG for deterministic tests; absent means `Math.random`. */
  seed?: number;
}

const DEFAULT_DELAY_MS = 400;

/**
 * Runs the pure engine client-side with no HTTP, behind a configurable delay, so every async
 * state is reachable and swapping in the live endpoint needs no host changes.
 *
 * Not `@Injectable` — only ever built via `new`, so AOT must not analyze its config-object
 * constructor parameter as a DI token.
 */
export class SiteSimulationMockRepository extends SiteSimulationRepository {
  readonly #delayMs: number;
  readonly #mode: SiteSimulationMockMode;
  readonly #seed?: number;

  constructor(config: SiteSimulationMockConfig = {}) {
    super();
    this.#delayMs = config.delayMs ?? DEFAULT_DELAY_MS;
    this.#mode = config.mode ?? 'data';
    this.#seed = config.seed;
  }

  runSimulation(request: SimulationRequest): Observable<SimulationResult> {
    if (this.#mode === 'error') {
      // `delay` would pass the error notification through immediately, leaving no observable
      // loading state.
      return timer(this.#delayMs).pipe(
        mergeMap(() =>
          throwError(() => new Error('site-simulation mock error')),
        ),
      );
    }
    const random =
      this.#seed !== undefined ? createSeededRandom(this.#seed) : Math.random;
    // `defer` so a synchronous engine throw (the shares-sum guard) arrives as an error
    // notification instead of escaping before the stream exists and hanging the loading state.
    return defer(() => of(runSimulation(request, random))).pipe(
      map((result) =>
        this.#mode === 'empty' ? { ...result, years: [] } : result,
      ),
      delay(this.#delayMs),
    );
  }
}

/** Swapping in a live repository is a change to this provider only. */
export function provideSiteSimulationMock(
  config?: SiteSimulationMockConfig,
): Provider {
  return {
    provide: SiteSimulationRepository,
    useFactory: () => new SiteSimulationMockRepository(config),
  };
}
